import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useDataProvider, useNotify, usePermissions } from 'react-admin';
import { PERMISSIONS } from '../../constants';
import { Tooltip, PieChart, Pie, Cell, Legend } from "recharts";
import { formatReal, getChainId, getEmployeeType, getAttendantId } from '../../lib';
import useDashboardContext from '../../hooks/useDashboardContext';
import { DashboardWrapper } from '../../components';
import { EmployeeType } from '../../models';

const COLORS = [
    { start: '#484A7F', end: '#7869DF' },
    { start: '#EF6937', end: '#FF9A60' },
    { start: '#299B22', end: '#79CF62' },
    { start: '#D6D6D6', end: '#232323' },
    { start: '#EFB810', end: '#FFD34A' }
];

const RADIAN = Math.PI / 180;

const legendWrapperStyle = {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#333',
    paddingBottom: '10px',
};

const tooltipDivStyle = {
    backgroundColor: '#fff',
    border: '1px solid #ccc',
    borderRadius: '5px',
    padding: '10px',
    fontSize: '14px',
    color: '#333',
};

const tooltipWrapperStyle = { visibility: 'visible', pointerEvents: 'auto' };

const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.7;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
        <text
            x={x} y={y} fill="#333"
            textAnchor={x > cx ? 'start' : 'end'}
            dominantBaseline="central"
            fontSize="12px" fontWeight="bold"
        >
            {`${(percent * 100).toFixed(0)}%`}
        </text>
    );
};

const gradientDefs = COLORS.map((gradient, index) => (
    <linearGradient key={index} id={`gradient${index}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={gradient.start} />
        <stop offset="100%" stopColor={gradient.end} />
    </linearGradient>
));

export const FuelChart = () => {
    const [showTooltip, setShowTooltip] = useState(false);

    const { permissions } = usePermissions();
    const dataProvider = useDataProvider();
    const { from, to, chain, origin, stationIds, fuelData, fuelDataLoading, setFuelData, setFuelDataLoading } = useDashboardContext();
    const notify = useNotify();

    const chainId = permissions && permissions.includes(PERMISSIONS.ADMIN) ? chain : getChainId();

    const hideTooltip = useCallback(() => setShowTooltip(false), []);
    const showTooltipFn = useCallback(() => setShowTooltip(true), []);

    useEffect(() => {
        if (fuelDataLoading) return;

        const filter = { from, to, origin };
        if (stationIds && stationIds.length > 0) {
            filter.stationIds = stationIds;
        }
        if (getEmployeeType() === EmployeeType.attendant) {
            filter.attendantId = getAttendantId();
        }

        if (permissions && permissions.includes(PERMISSIONS.FUEL_CHART)) {
            setFuelDataLoading(true);
            dataProvider.getList('dashboard/fillin-by-fuel', {
                filter, chainId,
                sort: { field: 'totalValue', order: 'DESC' },
                pagination: { page: 1, perPage: 100 },
            })
                .then(response => { setFuelData(response.data); })
                .catch(() => {
                    notify('Erro ao carregar gráfico de combustíveis', { type: 'warning' });
                    setFuelData([]);
                })
                .finally(() => { setFuelDataLoading(false); });
        }
    }, [permissions, from, to, chain, origin, stationIds]);

    const chartData = useMemo(() => {
        if (!fuelData) return [];
        return fuelData
            .filter(f => f.totalValue > 0 && f.id !== '0')
            .sort((a, b) => b.totalValue - a.totalValue)
            .slice(0, 5)
            .map(f => ({ name: f.name, value: f.totalValue }));
    }, [fuelData]);

    const tooltipContentRenderer = useCallback(({ payload }) =>
        payload && payload.length > 0 ? (
            <div style={tooltipDivStyle}>
                {`${payload[0].name}: ${formatReal(payload[0].value)}`}
            </div>
        ) : null
    , []);

    const chartContent = useMemo(() => (
        <PieChart onMouseLeave={hideTooltip}>
            <Legend
                layout="horizontal" verticalAlign="top" align="center"
                iconType="circle" wrapperStyle={legendWrapperStyle}
            />
            <Pie
                onMouseEnter={showTooltipFn}
                dataKey="value" startAngle={360} endAngle={0}
                data={chartData} cx="50%" cy="50%" outerRadius="85%"
                label={renderCustomizedLabel} labelLine={false}
                isAnimationActive={true}
            >
                {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={`url(#gradient${index})`} stroke="#fff" strokeWidth={2} />
                ))}
            </Pie>
            <defs>{gradientDefs}</defs>
            {showTooltip && (
                <Tooltip content={tooltipContentRenderer} wrapperStyle={tooltipWrapperStyle} />
            )}
        </PieChart>
    ), [chartData, showTooltip, hideTooltip, showTooltipFn, tooltipContentRenderer]);

    return (
        <DashboardWrapper
            permission={PERMISSIONS.FUEL_CHART}
            width="100%"
            link="/report-fillins"
            title="Venda de Combustíveis"
            aspectRatio={5.5 / 2.5}
            count={chartData?.length || 0}
            loading={fuelData === null}
            content={chartContent}
        />
    );
};
